import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Fragment,
} from "@stencil/core";

import { StzhFooterLocalizedText } from './stzh-footer.localization';

import { hasSlot, setPropsIfNull } from "../../utils/utils";

/**
 * @slot menu-link - Slot for menu links (`stzh-link` elements)
 * @slot logo - Slot for logo `img`
 * @slot info - Slot for info content (`stzh-link` or `stzh-text` elements)
 * @slot link - Slot for meta links (`stzh-link` elements)
 */
@Component({
  tag: "stzh-footer",
  styleUrl: "stzh-footer.scss"
})
export class StzhFooter {
  /** Translation strings */
  @Prop() localization: StzhFooterLocalizedText;

  /** Copyright text (will show default copyright string if undefined) */
  @Prop() copyright: string;

  /** Footer variant */
  @Prop() variant: "default" | "egov" = "default";

  /** Main URL for shortlinks */
  @Prop() mainUrl: string = "https://www.stadt-zuerich.ch";

  @Element() element: HTMLStzhFooterElement;

  private observer: MutationObserver;

  private init = () => {
    const menuLinks = this.element.querySelectorAll('stzh-link[slot="menu-link"], [slot="menu-link"] stzh-link');
    const links = this.element.querySelectorAll('stzh-link[slot="link"], [slot="link"] stzh-link');
    const infoTexts = this.element.querySelectorAll('stzh-text[slot="info"], [slot="info"] stzh-text');
    const infoLinks = this.element.querySelectorAll('stzh-link[slot="info"], [slot="info"] stzh-link');

    menuLinks.forEach((link) => {
      link.setAttribute("role", "listitem");

      setPropsIfNull(link, {
        size: "inherit"
      } as HTMLStzhLinkElement);
    });

    links.forEach((link) => {
      link.setAttribute("role", "listitem");

      setPropsIfNull(link, {
        size: "inherit"
      } as HTMLStzhLinkElement);
    });

    infoLinks.forEach((link) => {
      setPropsIfNull(link, {
        size: "inherit"
      } as HTMLStzhLinkElement);
    });

    infoTexts.forEach((link) => {
      setPropsIfNull(link, {
        curve: "none"
      } as HTMLStzhTextElement);
    });
  }

  async componentWillLoad() {
    if (typeof this.copyright === "undefined") {
      const currentDate = new Date();
      this.copyright = `© ${currentDate.getFullYear()} Stadt Zürich`;
    }

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, 'footer');
    }
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const logoSlotUsed = hasSlot(this.element, "logo");
    const infoSlotUsed = hasSlot(this.element, "info");
    const linkSlotUsed = hasSlot(this.element, "link");
    const menuLinkSlotUsed = hasSlot(this.element, "menu-link");

    const classes = {
      "stzh-footer": true,
      "stzh-footer--has-logo": logoSlotUsed,
      "stzh-footer--has-info": infoSlotUsed,
      "stzh-footer--has-links": linkSlotUsed,
      "stzh-footer--has-menu-links": menuLinkSlotUsed
    };

    return (
      <Host>
        <footer class={classes}>
          <div class="stzh-footer__menu">
            <div class="stzh-footer__menu-inner">
              <slot name="menu-link"></slot>
            </div>
          </div>
          <div class="stzh-footer__wrapper">
            <div class="stzh-footer__copyright-wrapper">
              {this.copyright &&
                <div class="stzh-footer__copyright">
                  {this.copyright}
                </div>
              }
              <div class="stzh-footer__logo">
                <slot name="logo"></slot>
              </div>
            </div>

            <div class="stzh-footer__info">
              <slot name="info"></slot>
            </div>

            <div class="stzh-footer__links" role="list">
              <slot name="link">
                {this.variant === "egov" &&
                  <Fragment>
                    <stzh-link
                      slot="link"
                      size="inherit"
                      target="_blank"
                      class="stzh-footer__link"
                      href={`${this.mainUrl}/${this.localization.legalNoticeUrl}`}
                    >
                      {this.localization.legalNoticeText}
                    </stzh-link>
                    <stzh-link
                      slot="link"
                      size="inherit"
                      target="_blank"
                      class="stzh-footer__link"
                      href={`${this.mainUrl}/${this.localization.imprintUrl}`}
                    >
                      {this.localization.imprintText}
                    </stzh-link>
                    <stzh-link
                      slot="link"
                      size="inherit"
                      class="stzh-footer__link"
                      target="_blank"
                      href={`${this.mainUrl}/${this.localization.accessibilityUrl}`}
                    >
                      {this.localization.accessibilityText}
                    </stzh-link>
                  </Fragment>
                }
              </slot>
            </div>
          </div>
        </footer>
      </Host>
    );
  }
}
